import { useEffect, useState } from 'react'
import { supabase } from '../../../../lib/supabase'

const CLUB_ID = 'a0000000-0000-0000-0000-000000000001'

const inputClass = "bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-3 py-2 text-sm outline-none transition-colors w-full"

export default function CategoriesManager() {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [newCategory, setNewCategory] = useState('')
  const [addingCategory, setAddingCategory] = useState(false)
  const [expandedCategory, setExpandedCategory] = useState(null)
  const [newTypes, setNewTypes] = useState({}) // { category_id: '' }
  const [addingTypes, setAddingTypes] = useState({}) // { category_id: bool }

  const fetchCategories = async () => {
    const { data } = await supabase
      .from('product_categories')
      .select('*, product_types(*)')
      .eq('club_id', CLUB_ID)
      .order('name')
    if (data) setCategories(data)
    setLoading(false)
  }

  useEffect(() => { fetchCategories() }, [])

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return
    setAddingCategory(true)
    await supabase.from('product_categories').insert({ club_id: CLUB_ID, name: newCategory.trim() })
    setNewCategory('')
    setAddingCategory(false)
    fetchCategories()
  }

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"? All item types in this category will also be deleted.`)) return
    await supabase.from('product_categories').delete().eq('id', cat.id)
    fetchCategories()
  }

  const handleAddType = async (categoryId) => {
    const name = newTypes[categoryId]?.trim()
    if (!name) return
    setAddingTypes(prev => ({ ...prev, [categoryId]: true }))
    await supabase.from('product_types').insert({ category_id: categoryId, name })
    setNewTypes(prev => ({ ...prev, [categoryId]: '' }))
    setAddingTypes(prev => ({ ...prev, [categoryId]: false }))
    fetchCategories()
  }

  const handleDeleteType = async (typeId) => {
    await supabase.from('product_types').delete().eq('id', typeId)
    fetchCategories()
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-5 sm:p-6">
        <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-1">Categories</div>
        <p className="text-gray-400 text-xs mb-5">
          Categories group your products. Each category can have item types (sub-classifications). The product form uses these dropdowns.
        </p>

        {loading ? (
          <div className="text-gray-400 text-sm py-6 text-center">Loading...</div>
        ) : (
          <div className="flex flex-col gap-2">
            {categories.length === 0 && (
              <div className="text-gray-400 text-sm py-6 text-center border border-dashed border-gray-200 dark:border-white/10">
                No categories yet. Add one below.
              </div>
            )}

            {categories.map(cat => (
              <div key={cat.id} className="border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5">
                {/* Category row */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 font-black text-sm text-gray-900 dark:text-white uppercase tracking-tight">
                    {cat.name}
                  </div>
                  <div className="text-xs text-gray-400 hidden sm:block">
                    {cat.product_types?.length || 0} type{cat.product_types?.length !== 1 ? 's' : ''}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      onClick={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
                      className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 font-bold uppercase tracking-widest transition-colors"
                    >
                      {expandedCategory === cat.id ? 'Hide Types' : 'Manage Types'}
                    </button>
                    <span className="text-gray-300 dark:text-white/20">|</span>
                    <button
                      onClick={() => handleDeleteCategory(cat)}
                      className="text-xs text-red-500 hover:text-red-400 font-bold uppercase tracking-widest transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Item types */}
                {expandedCategory === cat.id && (
                  <div className="px-4 pb-4 border-t border-gray-200 dark:border-white/10 pt-3">
                    <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-2">Item Types</div>

                    {cat.product_types?.length === 0 && (
                      <div className="text-gray-400 text-xs mb-3">No item types yet. Add one below.</div>
                    )}

                    <div className="flex flex-col gap-1.5 mb-3">
                      {cat.product_types?.map(type => (
                        <div key={type.id} className="flex items-center justify-between bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-3 py-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{type.name}</span>
                          <button
                            onClick={() => handleDeleteType(type.id)}
                            className="text-xs text-red-500 hover:text-red-400 font-bold uppercase tracking-widest transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Add type input */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newTypes[cat.id] || ''}
                        onChange={e => setNewTypes(prev => ({ ...prev, [cat.id]: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && handleAddType(cat.id)}
                        placeholder="e.g. Home Kit"
                        className={inputClass}
                      />
                      <button
                        onClick={() => handleAddType(cat.id)}
                        disabled={addingTypes[cat.id]}
                        className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap"
                      >
                        {addingTypes[cat.id] ? '...' : 'Add'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Add new category */}
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
                placeholder="New category name e.g. Jerseys"
                className={inputClass}
              />
              <button
                onClick={handleAddCategory}
                disabled={addingCategory || !newCategory.trim()}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 disabled:opacity-50 text-white px-5 py-2 text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap"
              >
                {addingCategory ? '...' : 'Add Category'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}